'use client';

import { useMemo } from 'react';

import type {
  PopStoreExtendedSubscriptionLevel,
  Subscriptions,
} from '@ps/models/lib/creator-subscription';
import {
  PopStoreExtendedSubscriptionLevel as ExtLevel,
  PopStoreSubscriptionLevel,
} from '@ps/models/lib/creator-subscription';
import { useCurrentUserProfile } from '@ps/ui/contexts/user-profile';

import { canConfigureSubscriptionLevel } from '../functions/canConfigureSubscriptionLevel';
import { getMaximumSubscriptionPrice } from '../functions/getMaximumSubscriptionPrice';
import { getMinimumSubscriptionPrice } from '../functions/getMinimumSubscriptionPrice';
import type { SubscriptionConfigurationItem } from '../types';

// Canonical display order. Server's `Subscriptions` object can
// come back keyed in any order, so we iterate the enum
// explicitly rather than relying on `Object.values` —
// otherwise the UI shows Fan/Subscriber/VIP in whatever order
// the server JSON happens to use and downstream `next-tier`
// validation reads the wrong neighbour.
const PAID_TIER_ORDER: PopStoreSubscriptionLevel[] = [
  PopStoreSubscriptionLevel.SUBSCRIBER,
  PopStoreSubscriptionLevel.FAN,
  PopStoreSubscriptionLevel.VIP,
];

/**
 * Build the ordered tier-row list rendered by
 * `ManageSubscriptionModal`. Order:
 *
 *   FOLLOWER → SUBSCRIBER → FAN → VIP
 *
 * FOLLOWER is synthesised from the creator's profile
 * (`followersSettings`) — it isn't a real paid tier and therefore
 * doesn't appear in the `Subscriptions` REST response. The other
 * three come from `useFetchAllSubscriptions`.
 *
 * Each row carries min/max price guard rails (FAN can't price
 * below SUBSCRIBER + 10¢, etc.) and a `disabled` flag wired to
 * the plan-gate rule (`canConfigureSubscriptionLevel`) AND the
 * age-verification gate. 1:1 with legacy
 * `SubscriptionSetupContent`'s `items` memo.
 */
export function useTierItems({
  subscriptions,
  isAgeVerified,
  defaultLevel,
}: {
  subscriptions: Subscriptions | undefined;
  isAgeVerified: boolean | undefined;
  defaultLevel?: PopStoreExtendedSubscriptionLevel;
}): SubscriptionConfigurationItem[] {
  const profile = useCurrentUserProfile();

  return useMemo<SubscriptionConfigurationItem[]>(() => {
    if (!subscriptions) return [];

    const list: SubscriptionConfigurationItem[] = [];

    // FOLLOWER row — synthesised from `followersSettings`. Never
    // disabled (followers are always free + always toggleable).
    list.push({
      defaultOpen: defaultLevel === ExtLevel.FOLLOWER,
      subscription: {
        level: ExtLevel.FOLLOWER,
        description: profile?.followersSettings?.description,
        active: profile?.followersSettings?.active !== false,
      },
    });

    for (const level of PAID_TIER_ORDER) {
      const subscription = subscriptions[level];
      if (!subscription) continue;
      // `platform_sub_level` is the narrow `PopStoreSubscriptionLevel`
      // enum; the consumer side uses the extended enum (which adds
      // FOLLOWER). The string values overlap, so an explicit cast
      // keeps the rest of the helper code generic.
      const extendedLevel =
        level as unknown as PopStoreExtendedSubscriptionLevel;
      list.push({
        defaultOpen: defaultLevel === extendedLevel,
        subscription: {
          level,
          subscription,
        },
        minSubscriptionPrice: getMinimumSubscriptionPrice(subscriptions, level),
        maxSubscriptionPrice: getMaximumSubscriptionPrice(subscriptions, level),
        disabled:
          !isAgeVerified ||
          !canConfigureSubscriptionLevel(subscriptions, level),
      });
    }

    return list;
  }, [
    subscriptions,
    isAgeVerified,
    defaultLevel,
    profile?.followersSettings?.description,
    profile?.followersSettings?.active,
  ]);
}
